import React, { useState } from 'react';
import type { CreateLeagueRequest, UpdateLeagueRequest } from '@sulis-liiga/common';
import { LeagueStatus } from '@sulis-liiga/common';

interface LeagueFormProps {
  initialData?: Partial<CreateLeagueRequest | UpdateLeagueRequest>;
  onSubmit: (data: CreateLeagueRequest | UpdateLeagueRequest) => void;
  onCancel: () => void;
  loading: boolean;
  isEdit?: boolean;
}

const LeagueForm: React.FC<LeagueFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading,
  isEdit = false,
}) => {
  const createData = initialData as CreateLeagueRequest;
  const updateData = initialData as UpdateLeagueRequest;

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    city: initialData?.location?.city || '',
    country: initialData?.location?.country || '',
    sportType: createData?.sportType || 'badminton',
    defaultPromotion: initialData?.settings?.promotionRelegationRules?.defaultPromotion || 2,
    defaultRelegation: initialData?.settings?.promotionRelegationRules?.defaultRelegation || 2,
    playerLimit: initialData?.settings?.playerLimit || 4,
    status: updateData?.status || LeagueStatus.PLANNING,
    registrationOpen: updateData?.registrationOpen ?? false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData: CreateLeagueRequest & Partial<UpdateLeagueRequest> = {
      name: formData.name,
      description: formData.description || undefined,
      location: {
        city: formData.city,
        country: formData.country,
      },
      settings: {
        promotionRelegationRules: {
          defaultPromotion: formData.defaultPromotion,
          defaultRelegation: formData.defaultRelegation,
        },
        playerLimit: formData.playerLimit,
      },
    };

    if (!isEdit) {
      submitData.sportType = formData.sportType;
    } else {
      submitData.status = formData.status;
      submitData.registrationOpen = formData.registrationOpen;
    }

    onSubmit(submitData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'number'
          ? parseInt(value)
          : type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          League Name
        </label>
        <input
          type="text"
          name="name"
          id="name"
          required
          value={formData.name}
          onChange={handleChange}
          className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          name="description"
          id="description"
          rows={3}
          value={formData.description}
          onChange={handleChange}
          className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
            City
          </label>
          <input
            type="text"
            name="city"
            id="city"
            required
            value={formData.city}
            onChange={handleChange}
            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700">
            Country
          </label>
          <input
            type="text"
            name="country"
            id="country"
            required
            value={formData.country}
            onChange={handleChange}
            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>
      </div>

      {!isEdit && (
        <div>
          <label htmlFor="sportType" className="block text-sm font-medium text-gray-700">
            Sport Type
          </label>
          <input
            type="text"
            name="sportType"
            id="sportType"
            required
            value={formData.sportType}
            onChange={handleChange}
            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div>
          <label htmlFor="playerLimit" className="block text-sm font-medium text-gray-700">
            Player Limit
          </label>
          <input
            type="number"
            name="playerLimit"
            id="playerLimit"
            required
            min="1"
            value={formData.playerLimit}
            onChange={handleChange}
            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="defaultPromotion" className="block text-sm font-medium text-gray-700">
            Default Promotion
          </label>
          <input
            type="number"
            name="defaultPromotion"
            id="defaultPromotion"
            required
            min="0"
            value={formData.defaultPromotion}
            onChange={handleChange}
            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="defaultRelegation" className="block text-sm font-medium text-gray-700">
            Default Relegation
          </label>
          <input
            type="number"
            name="defaultRelegation"
            id="defaultRelegation"
            required
            min="0"
            value={formData.defaultRelegation}
            onChange={handleChange}
            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>
      </div>

      {isEdit && (
        <>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              name="status"
              id="status"
              value={formData.status}
              onChange={handleChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              <option value={LeagueStatus.PLANNING}>Planning</option>
              <option value={LeagueStatus.ACTIVE}>Active</option>
              <option value={LeagueStatus.INACTIVE}>Inactive</option>
              <option value={LeagueStatus.COMPLETED}>Completed</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="registrationOpen"
              id="registrationOpen"
              checked={formData.registrationOpen}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label
              htmlFor="registrationOpen"
              className="ml-2 block text-sm font-medium text-gray-700"
            >
              Registration Open
            </label>
          </div>
        </>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : isEdit ? 'Update League' : 'Create League'}
        </button>
      </div>
    </form>
  );
};

export default LeagueForm;
